import type { AgentSession } from '@litlingo/client';
import { clearURLParams } from 'actions';
import EmptyTableState from 'components/EmptyTableState';
import FilterListHeaderRedesign from 'components/Filters/FilterListHeaderRedesign';
import ListFooterHooksBased from 'components/ListFooter/ListFooterHooksBased';
import LoadingIndicator from 'components/LoadingIndicator';
import Navbar from 'components/Navbar';
import SearchInput from 'components/SearchInput';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import useAgentSessions from 'hooks/agentSessions/useAgentSessions';
import useInvestigation from 'hooks/investigations/useInvestigation';
import React, { useState } from 'react';
import LoadingOverlay from 'react-loading-overlay-ts';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import logEvent from 'utils/analytics';
import AgentSessionsListCount from './AgentSessionsListCount';
import CreateAgentSessionModal from './CreateAgentSessionModal';
import SessionListRow from './SessionListRow';

const AgentSessionsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { investigationId } = useParams<{ investigationId: string }>();

  const { data: investigation } = useInvestigation(investigationId, { redirectOnError: true });
  const { data, isLoading, isFetching, refetch } = useAgentSessions(investigationId, {
    redirectOnError: true,
  });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const toggleCreateModal = (): void => {
    setIsCreateModalOpen(!isCreateModalOpen);
  };

  const handleResetFiltersClick = (): void => {
    logEvent('investigations-list-reset-filters');
    dispatch(clearURLParams());
  };

  return (
    <div className="min-h-screen min-w-screen bg-white">
      <Navbar />
      <div className="pt-6">
        <header>
          <div className="max-w-7xl mx-auto px-6">
            <div className="mt-2 md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0 flex items-center justify-between">
                <div className="flex flex-row items-center">
                  <h2 className="title">{investigation?.name} - Agent Sessions</h2>
                  {isFetching && <LoadingIndicator className="ml-2" size="5" />}
                </div>
                <div className="flex flex-row gap-2">
                  <button
                    type="button"
                    className="flex flex-row justify-center button button--primary h-8 w-40 rounded whitespace-no-wrap"
                    data-testid="create-investigation-button"
                    onClick={toggleCreateModal}
                  >
                    <span>Start New Session</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 flex-shrink-0 flex justify-between items-center">
              <div className="flex flex-row gap-2">
                <SearchInput
                  resource={resourceQueryParamName.agentSessions}
                  field="broad_search"
                  className="h-8"
                />
                <button
                  type="button"
                  className="bg-white border-0 focus:outline-none inline-flex items-end leading-4 text text-body text-litlingo-gray-6 hover:text-litlingo-gray-4 underline"
                  data-testid="reset-filters-button"
                  onClick={handleResetFiltersClick}
                >
                  Clear Filters
                </button>
              </div>
              <AgentSessionsListCount />
            </div>
          </div>
        </header>
        <main>
          <div className="h-full mt-2 mb-8 max-w-7xl mx-auto px-6">
            <LoadingOverlay
              active={isLoading}
              spinner={<LoadingIndicator size="10" />}
              fadeSpeed={0}
              styles={{
                content: (base) => ({
                  ...base,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }),
                wrapper: () => ({}),
                spinner: () => ({}),
                overlay: (base) => ({
                  ...base,
                  zIndex: 10,
                }),
              }}
            >
              <div className="table-wrapper__new h-full">
                <table className="table-wrapper__table h-full">
                  <thead>
                    <tr>
                      <th className="table-wrapper__new-th pl-4">Objective</th>
                      <th className="table-wrapper__new-th table-wrapper__th--w-8">Status</th>
                      <th className="table-wrapper__new-th table-wrapper__th--w-12">
                        Input Tokens
                      </th>
                      <th className="table-wrapper__new-th table-wrapper__th--w-12">
                        Output Tokens
                      </th>
                      <th className="table-wrapper__new-th table-wrapper__th--w-6">Steps</th>
                      <FilterListHeaderRedesign
                        resource={resourceQueryParamName.agentSessions}
                        text="Last Modified"
                        field="updated_at"
                        className="table-wrapper__new-th table-wrapper__th--w-12"
                      />
                      <th className="table-wrapper__new-th table-wrapper__th--w-14">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="table-wrapper__tbody h-full">
                    {!isFetching && data && data.records.length === 0 && (
                      <EmptyTableState
                        data-testid="empty-table-state"
                        message="There are no investigations to display"
                        colCount="7"
                      />
                    )}
                    {investigation &&
                      data?.records?.map((row: AgentSession) => (
                        <SessionListRow
                          key={row.uuid}
                          investigation={investigation}
                          session={row}
                        />
                      ))}
                  </tbody>
                </table>
                <ListFooterHooksBased
                  resourceStateName={resourceQueryParamName.agentSessions}
                  resourceName="agent sessions"
                  fetchHook={useAgentSessions}
                />
              </div>
            </LoadingOverlay>
          </div>
        </main>
      </div>
      {isCreateModalOpen && (
        <CreateAgentSessionModal
          isModalOpen={isCreateModalOpen}
          toggleModalOpen={toggleCreateModal}
          onCreated={(): void => {
            refetch().catch(() => undefined);
          }}
        />
      )}
    </div>
  );
};

export default AgentSessionsPage;
